import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Pagination,
  List,
  ListItem,
  Button,
  Autocomplete,
  TextField,
  Tooltip, 
  MenuItem, Select, FormControl, InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton
} from '@mui/material';

import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew'; // Icon pang navigate
import buxferTransactions from '../services/buxfer.service';

export default function Sidebar({ selectedUserName, onAccountSelect, onBankSelect }) {
  const [userList, setUserList] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);
  const [search, setSearch] = useState('');
  const [keywordInput, setKeywordInput] = useState('');
  const [keywordMode, setKeywordMode] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [keywordList, setKeywordList] = useState([]);
  const [sortBy, setSortBy] = useState('');
  const [sortDirection, setSortDirection] = useState('ASC');

  useEffect(() => {
    // buxferTransactions.getUniqueLoginId().then((resp) => {
    //   if (resp.success) {
    //     setUserList(resp.result);
    //     setKeywordList(resp.result.map(x => x.login_id));
    //   }
    // });
  }, []);

  const fetchAccounts = async () => {
    if (!selectedUser && !keywordMode && !search) return;
    setLoading(true);
    try {
      const user = keywordMode ? null : selectedUser;
      const keyword = keywordMode ? keywordInput : search;
      console.log(user, keyword)
      const res = await buxferTransactions.getAccounts(user, page, null, keyword, sortBy || null, sortDirection);
      if (res.success) {
        setAccounts(res.result.accounts);
        setTotalPages(res.result.totalPages);
      }
    } catch (err) {
      console.error('Error fetching user accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [selectedUser, keywordMode]);

  useEffect(() => {
    fetchAccounts();
  }, [keywordInput, keywordMode, page, sortBy, sortDirection]);

  const handlePageChange = (_, value) => {
    setPage(value);
  };

  const selectedUserData = useMemo(() => {
    return userList.find((u) => u.login_id === selectedUser);
  }, [userList, selectedUser]);

  const handleSearch = () => {
    setPage(1);
    setKeywordMode(true);
    fetchAccounts();
  };

  const clearSelectedUser = () => {
    setSelectedUser(null);
    setInputValue('');
    setSearch('');
    setKeywordInput('');
    setKeywordMode(false);
    setIsFiltered(false);
    setSelectedAccountId(null);
    onBankSelect('');
    setAccounts([]);
  };

  const groupedAccounts = accounts.reduce((acc, a) => {
    const t = a.type && a.type.toString().trim() !== '' ? a.type : 'Others';
    if (!acc[t]) acc[t] = [];
    acc[t].push(a);
    return acc;
  }, {});

  // Function to open account page
  const handleOpenAccountPage = (e, accountName) => {
    e.stopPropagation(); // Stop click from triggering filter
    // Use accountName param so the BuxferManagerPage applies the correct account filter on load
    const url = `/buxfer-accounts?accountName=${encodeURIComponent(accountName)}`;
    window.open(url, '_blank');
  };

  return (
    <Box sx={{ p: 2 }}>
      {!selectedUser && (
        <>
          <Box sx={{ mb: 2 }}>
            <Autocomplete
              freeSolo
              fullWidth
              size="small"
              options={keywordList}
              inputValue={keywordInput}
              onInputChange={(e, val) => setKeywordInput(val)}
              onChange={(e, val) => {
                if (val) {
                  setKeywordInput(val);
                  setKeywordMode(true);
                  setPage(1);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Account"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSearch();
                  }}
                />
              )}
              sx={{ mb: 1 }}
            />
            <Button fullWidth variant="contained" onClick={handleSearch}>
              Search
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="balance">Balance</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Direction</InputLabel>
              <Select
                value={sortDirection}
                label="Direction"
                onChange={(e) => setSortDirection(e.target.value)}
              >
                <MenuItem value="ASC">ASC</MenuItem>
                <MenuItem value="DESC">DESC</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {selectedUserData?.notSyncingCount > 0 && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => {
              window.location.href = `/buxfer/not-synced/${selectedUser}`;
            }}
            startIcon={<ErrorIcon />}
            sx={{
              textTransform: 'none', borderRadius: '16px', fontWeight: 500, px: 2, py: 0.5, fontSize: '0.8rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)', '&:hover': { backgroundColor: '#ffe6e6' }
            }}
          >
            Not Syncing ({selectedUserData.notSyncingCount})
          </Button>
        )}

        {(selectedUser || keywordMode) && (
          <Button
            size="small"
            variant="outlined"
            onClick={clearSelectedUser}
            sx={{
              textTransform: 'none', borderRadius: '16px', fontWeight: 500, px: 2, py: 0.5, fontSize: '0.8rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
          >
            Clear
          </Button>
        )}
      </Box>

      {(selectedUser || keywordMode) && (
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Accounts {selectedUser ? `under ${selectedUser}` : `matching "${keywordInput}"`}
          </Typography>

          {loading ? (
            <CircularProgress size={20} />
          ) : (
            <>
              {Object.keys(groupedAccounts).length === 0 && (
                <Typography variant="body2" align="center" color="text.secondary">
                  No accounts found.
                </Typography>
              )}

              {Object.entries(groupedAccounts).map(([typeLabel, items]) => (
                <Accordion key={typeLabel} defaultExpanded sx={{ mb: 1, boxShadow: 'none', '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { margin: '8px 0' } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {typeLabel}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {items.length}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <List dense>
                      {items.map((acc) => (
                        <ListItem
                          key={acc.id}
                          button
                          onClick={() => {
                            if(selectedAccountId !== acc.id){
                              setSelectedAccountId(acc.id);
                              onBankSelect(acc.name);
                            } else {
                              setSelectedAccountId(null);
                              onBankSelect('');
                            }
                          }}
                          selected={selectedAccountId === acc.id}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column', // Stack contents vertically
                            alignItems: 'flex-start', // Align to left
                            px: 1.5,
                            py: 1,
                            borderRadius: 2,
                            mb: 0.5,
                            border: '1px solid transparent',
                            ...(selectedAccountId === acc.id && {
                              bgcolor: 'primary.50', // Lighter selection color
                              borderColor: 'primary.200',
                              '&:hover': { bgcolor: 'primary.100' },
                            }),
                            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
                          }}
                        >
                          {/* ROW 1: Name & Not Synced Icon */}
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 0.5 }}>
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    fontWeight: 600, 
                                    lineHeight: 1.2, 
                                    flexGrow: 1, 
                                    wordBreak: 'break-word',
                                    color: selectedAccountId === acc.id ? 'primary.main' : 'text.primary'
                                }}
                            >
                              {acc.name}
                            </Typography>
                            {acc.notSynced && (
                              <Tooltip title={acc.lastSynced ? `Last synced: ${new Date(acc.lastSynced).toLocaleString()}` : 'Never'}>
                                <ErrorIcon color="error" sx={{ fontSize: 16, ml: 0.5 }} />
                              </Tooltip>
                            )}
                          </Box>

                          {/* ROW 2: Balance & Action Button (Space Between) */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            
                            {/* Balance */}
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    fontWeight: 700, 
                                    color: acc.balance < 0 ? 'error.main' : 'success.main',
                                    fontSize: '0.85rem'
                                }}
                            >
                              {acc.balance}
                            </Typography>

                            {/* Open Button */}
                            <Tooltip title="View Full Account Details">
                                <IconButton 
                                    size="small" 
                                    onClick={(e) => handleOpenAccountPage(e, acc.name)}
                                    sx={{ 
                                        padding: 0.5,
                                        color: 'text.secondary',
                                        '&:hover': { 
                                            color: 'primary.main',
                                            bgcolor: 'action.hover' 
                                        }
                                    }}
                                >
                                    <OpenInNewIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>

                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              ))}

              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                size="small"
                sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}
              />
            </>
          )}
        </Box>
      )}
    </Box>
  );
}